
/* ============================================================
   InfinityDots3D — an infinity mark formed entirely out of points of light.

   There is no drawn shape. Thousands of dots travel through a 3D tube that
   follows the infinity, one strand passing in front of the other where they
   cross. Depth comes from perspective, focus (near and far dots soften and
   dim), colour (far = deeper violet, near = lighter) and a slow sway.

   WebGL, written directly — no three.js, no libraries, nothing loaded from
   another domain. Transparent background, so it sits on any section.

   USE
     1. Load this file once on the page (a normal deferred script tag).
     2. Put a canvas with the attribute  data-infinity-3d  inside a box that
        has a size, e.g. a div with  aspect-ratio: 16 / 9.
        Give the canvas  display:block; width:100%; height:100%.

     Options as attributes:  data-count="4000"  data-loop="45"  data-sway="0.5"
     or in code:
       var mark = InfinityDots3D(canvasElement, { count: 4000, loop: 45 });
       mark.pause(); mark.play(); mark.seek(12.5); mark.destroy();

   THE LOOP
     Travel, spiralling, shimmer and the camera sway all repeat a whole number
     of times per `loop` seconds, so second 0 and second `loop` are the same
     frame. (Pointer tilt is extra and only applies while the mouse moves.)
   ============================================================ */
(function (global) {
  'use strict';

  var DEFAULTS = {
    maxDpr: 2,
    count: 6000,          // number of dots (fewer are drawn automatically on small canvases)
    loop: 60,             // seconds for one full, exactly repeating loop (bigger = slower)
    aspect: 2.6,          // width / height of the infinity's centre line
    roundness: 0.22,      // 0 = round, open lobes · 1 = classic pointed lemniscate
    thickness: 0.085,     // tube radius, as a fraction of the mark's half-width
    depth: 0.15,          // how far the strands separate front-to-back where they cross
    direction: 1,         // 1 or -1: which way the dots travel
    spin: 1,              // how much the dots spiral around the tube (0 = straight flow)
    sway: 1,              // camera sway multiplier (0 = perfectly still camera)
    focus: 1,             // depth-of-field strength (0 = everything sharp)
    size: 1,              // dot size multiplier
    twinkle: 1,           // 0 = steady light · 1 = full shimmer
    interactive: 1,       // 1 = tilts slightly towards the mouse · 0 = off
    glow: '176,128,255',  // main dot colour, as r,g,b
    accent: '240,54,155', // colour of the few accent dots, as r,g,b
    padding: 0.06,        // empty margin, as a fraction of the canvas width
    seed: 11              // change for a different arrangement of dots
  };

  var TAU = Math.PI * 2;
  var SAMPLES = 1024;     // points along the centre line
  var CAM = 3.4;          // camera distance, in half-widths of the mark

  var VERT = [
    'attribute vec3 aPos;',
    'attribute vec4 aLook;',     // size, whiteness of core, accent 0/1, brightness
    'attribute vec2 aTw;',       // shimmer: cycles per loop, phase
    'uniform float uT, uPx, uCam, uDof, uTwinkle, uSize, uMax, uFade;',
    'uniform vec2 uRot, uScale;',
    'uniform vec3 uGlow, uAccent;',
    'varying float vA, vBlur, vWhite;',
    'varying vec3 vCol;',
    'void main(){',
    '  float cy = cos(uRot.x), sy = sin(uRot.x), cx = cos(uRot.y), sx = sin(uRot.y);',
    '  vec3 p = aPos;',
    '  p = vec3(p.x * cy + p.z * sy, p.y, -p.x * sy + p.z * cy);',
    '  p = vec3(p.x, p.y * cx - p.z * sx, p.y * sx + p.z * cx);',
    '  float persp = uCam / (uCam - p.z);',
    '  gl_Position = vec4(p.xy * uScale * persp, 0.0, 1.0);',
    '  float coc = uDof * abs(p.z - 0.10);',                       // out-of-focus amount (focus sits toward the front)
    '  float size = aLook.x * uSize * uPx * persp * (1.0 + coc);',
    '  gl_PointSize = clamp(size, 1.0, uMax);',
    '  float tw = 0.5 + 0.5 * sin(6.2831853 * aTw.x * uT + aTw.y);',
    '  float near = smoothstep(-0.55, 0.55, p.z);',
    '  float a = aLook.w * (1.0 - uTwinkle * 0.55 * (1.0 - tw)) * mix(0.58, 1.25, near) / (1.0 + coc * coc * 1.3);',
    '  vA = a * min(1.0, size) * uFade;',
    '  vBlur = aLook.y < 0.0 ? 1.0 : clamp(coc, 0.0, 1.0);',        // haze is always soft
    '  vWhite = max(aLook.y, 0.0);',
    '  vec3 far = uGlow * vec3(0.62, 0.52, 1.0);',
    '  vec3 close = mix(uGlow, vec3(1.0), 0.45);',
    '  vec3 c = near < 0.5 ? mix(far, uGlow, near * 2.0) : mix(uGlow, close, (near - 0.5) * 2.0);',
    '  vCol = mix(c, uAccent, aLook.z);',
    '}'
  ].join('\n');

  var FRAG = [
    'precision mediump float;',
    'varying float vA, vBlur, vWhite;',
    'varying vec3 vCol;',
    'void main(){',
    '  float d = length(gl_PointCoord - 0.5) * 2.0;',
    '  if (d > 1.0) discard;',
    '  float halo = pow(1.0 - d, 2.2);',
    '  float core = smoothstep(0.42, 0.08, d);',
    '  float sharp = halo * 0.55 + core * 0.85;',                  // in focus: bright core, soft halo
    '  float soft = smoothstep(1.0, 0.5, d) * 0.5;',               // out of focus: flat, dim disc
    '  float a = mix(sharp, soft, vBlur) * vA;',
    '  vec3 col = mix(vCol, vec3(1.0), core * vWhite * (1.0 - vBlur * 0.7));',
    '  gl_FragColor = vec4(col * a, a);',
    '}'
  ].join('\n');

  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function rgb(str) { var p = String(str).split(','); return [p[0] / 255, p[1] / 255, p[2] / 255]; }

  function InfinityDots3D(canvas, options) {
    var cfg = {}, key;
    for (key in DEFAULTS) cfg[key] = options && options[key] !== undefined ? options[key] : DEFAULTS[key];

    var glOpts = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false };
    var gl = canvas.getContext('webgl', glOpts) || canvas.getContext('experimental-webgl', glOpts);
    if (!gl) {
      if (global.console) console.warn('InfinityDots3D: WebGL is not available, nothing drawn.');
      return { play: function () {}, pause: function () {}, seek: function () {}, destroy: function () {} };
    }

    /* ---- the centre line of the infinity, in 3D, evenly spaced ---- */
    var P = new Float32Array(SAMPLES * 3), Nn = new Float32Array(SAMPLES * 3), Bn = new Float32Array(SAMPLES * 3);
    (function curve() {
      var N = 4096, raw = new Float32Array((N + 1) * 3), len = new Float32Array(N + 1);
      var k = cfg.roundness, peak = 0, j, th, s, c, d;
      for (j = 0; j <= N; j++) {
        th = j / N * TAU; s = Math.sin(th); c = Math.cos(th); d = 1 + k * s * s;
        raw[j * 3] = c / d; raw[j * 3 + 1] = s * c / d;
        raw[j * 3 + 2] = cfg.depth * s;                 // one strand rises, the other dips, at the crossing
        if (Math.abs(raw[j * 3 + 1]) > peak) peak = Math.abs(raw[j * 3 + 1]);
      }
      var sy = (1 / cfg.aspect) / peak;
      for (j = 0; j <= N; j++) raw[j * 3 + 1] *= sy;
      len[0] = 0;
      for (j = 1; j <= N; j++) {
        len[j] = len[j - 1] + Math.hypot(raw[j * 3] - raw[j * 3 - 3], raw[j * 3 + 1] - raw[j * 3 - 2], raw[j * 3 + 2] - raw[j * 3 - 1]);
      }
      var total = len[N], at = 0, m, f;
      for (j = 0; j < SAMPLES; j++) {
        var want = j / SAMPLES * total;
        while (at < N - 1 && len[at + 1] < want) at++;
        f = (want - len[at]) / (len[at + 1] - len[at] || 1);
        for (m = 0; m < 3; m++) P[j * 3 + m] = raw[at * 3 + m] + (raw[at * 3 + 3 + m] - raw[at * 3 + m]) * f;
      }
      for (j = 0; j < SAMPLES; j++) {                   // a frame around each point, to build the tube
        var a = ((j + SAMPLES - 1) % SAMPLES) * 3, b = ((j + 1) % SAMPLES) * 3;
        var tx = P[b] - P[a], ty = P[b + 1] - P[a + 1], tz = P[b + 2] - P[a + 2];
        var tl = Math.hypot(tx, ty, tz) || 1; tx /= tl; ty /= tl; tz /= tl;
        var nx = ty, ny = -tx, nl = Math.hypot(nx, ny) || 1; nx /= nl; ny /= nl;   // sideways, in the plane
        Nn[j * 3] = nx; Nn[j * 3 + 1] = ny; Nn[j * 3 + 2] = 0;
        Bn[j * 3] = ty * 0 - tz * ny; Bn[j * 3 + 1] = tz * nx - tx * 0; Bn[j * 3 + 2] = tx * ny - ty * nx;
      }
    })();

    /* ---- the dots ---- */
    var n = Math.max(1, cfg.count | 0), rand = rng(cfg.seed), i;
    var u0 = new Float32Array(n), au = new Float32Array(n), fu = new Float32Array(n), pu = new Float32Array(n);
    var rr = new Float32Array(n), fr = new Float32Array(n), pr = new Float32Array(n);
    var ph = new Float32Array(n), sp = new Float32Array(n);
    var pos = new Float32Array(n * 3), look = new Float32Array(n * 4), tw = new Float32Array(n * 2);
    for (i = 0; i < n; i++) {
      var stray = rand() < 0.06, kind = rand();
      u0[i] = rand();
      au[i] = 0.003 + rand() * 0.010; fu[i] = 1 + Math.floor(rand() * 3); pu[i] = rand() * TAU;
      rr[i] = stray ? 1 + Math.pow(rand(), 2) * 1.5 : Math.sqrt(rand());      // filled tube + a little dust around it
      fr[i] = 1 + Math.floor(rand() * 4); pr[i] = rand() * TAU;
      ph[i] = rand() * TAU;
      sp[i] = (rand() < 0.5 ? -1 : 1) * (1 + Math.floor(rand() * 2));          // whole turns around the tube per loop
      var haze = !stray && rand() < 0.03;
      var size = haze ? 44 + rand() * 30 : kind < 0.80 ? 2.6 + rand() * 2.8 : kind < 0.97 ? 5.5 + rand() * 4.5 : 12 + rand() * 8;
      var bright = haze ? 0.05 + rand() * 0.05 : kind < 0.80 ? 0.5 + rand() * 0.45 : kind < 0.97 ? 0.75 + rand() * 0.25 : 1;
      if (haze) rr[i] *= 0.6;
      look[i * 4] = size;
      look[i * 4 + 1] = haze ? -1 : kind < 0.80 ? 0.4 + rand() * 0.4 : 0.85 + rand() * 0.15;
      look[i * 4 + 2] = !haze && rand() < 0.07 ? 1 : 0;
      look[i * 4 + 3] = bright * (stray ? 0.5 : 1);
      tw[i * 2] = 4 + Math.floor(rand() * 9); tw[i * 2 + 1] = rand() * TAU;
    }

    /* ---- GPU set-up (repeated if the browser ever drops the context) ---- */
    var prog, bufPos, U = {}, maxPoint = 64, buffers = [], shaders = [];
    function shader(type, src) {
      var s = gl.createShader(type); shaders.push(s); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    function attrib(name, data, width, usage) {
      var b = gl.createBuffer(); buffers.push(b); var loc = gl.getAttribLocation(prog, name);
      gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, data, usage);
      gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, width, gl.FLOAT, false, 0, 0);
      return b;
    }
    function setup() {
      buffers = []; shaders = [];
      prog = gl.createProgram();
      gl.attachShader(prog, shader(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
      gl.useProgram(prog);
      attrib('aLook', look, 4, gl.STATIC_DRAW);
      attrib('aTw', tw, 2, gl.STATIC_DRAW);
      bufPos = attrib('aPos', pos, 3, gl.DYNAMIC_DRAW);
      ['uT', 'uPx', 'uCam', 'uDof', 'uTwinkle', 'uSize', 'uMax', 'uFade', 'uRot', 'uScale', 'uGlow', 'uAccent']
        .forEach(function (name) { U[name] = gl.getUniformLocation(prog, name); });
      var range = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE);
      maxPoint = range && range[1] ? range[1] : 64;
      gl.disable(gl.DEPTH_TEST);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE);          // light adds up
      gl.clearColor(0, 0, 0, 0);
      var g = rgb(cfg.glow), a = rgb(cfg.accent);
      gl.uniform3f(U.uGlow, g[0], g[1], g[2]); gl.uniform3f(U.uAccent, a[0], a[1], a[2]);
      gl.uniform1f(U.uCam, CAM);
    }

    /* ---- size ---- */
    var W = 1, H = 1, DPR = 1, halfPx = 1, drawn = n;
    function resize() {
      var box = canvas.getBoundingClientRect();
      W = Math.max(1, box.width); H = Math.max(1, box.height);
      DPR = Math.min(global.devicePixelRatio || 1, cfg.maxDpr);
      canvas.width = Math.round(W * DPR); canvas.height = Math.round(H * DPR);
      gl.viewport(0, 0, canvas.width, canvas.height);
      var pad = cfg.padding * W, room = 1.12;                      // headroom for sway and perspective
      halfPx = Math.min((W / 2 - pad) / ((1 + cfg.thickness) * room), (H / 2 - pad) / ((1 / cfg.aspect + cfg.thickness + cfg.depth) * room));
      halfPx = Math.max(halfPx, 8);
      drawn = Math.max(1, Math.round(n * Math.max(0.5, Math.min(1, W / 760))));   // lighter on small canvases
    }

    /* ---- one frame, for any moment in time ---- */
    var tiltX = 0, tiltY = 0, aimX = 0, aimY = 0, fade = 0;
    function draw(seconds) {
      var t = seconds / cfg.loop; t -= Math.floor(t);
      var dir = cfg.direction < 0 ? -1 : 1, R = cfg.thickness, spin = cfg.spin;
      for (var i = 0; i < drawn; i++) {
        var u = u0[i] + dir * t + au[i] * Math.sin(TAU * fu[i] * t + pu[i]);
        u -= Math.floor(u);
        var f = u * SAMPLES, a = f | 0, b = (a + 1 === SAMPLES ? 0 : a + 1) * 3; f -= a; a *= 3;
        var ang = ph[i] + TAU * sp[i] * spin * t;
        var r = R * rr[i] * (1 + 0.10 * Math.sin(TAU * fr[i] * t + pr[i]));
        var cn = Math.cos(ang) * r, sn = Math.sin(ang) * r, o = i * 3;
        pos[o]     = P[a]     + (P[b]     - P[a])     * f + (Nn[a]     + (Nn[b]     - Nn[a])     * f) * cn + (Bn[a]     + (Bn[b]     - Bn[a])     * f) * sn;
        pos[o + 1] = P[a + 1] + (P[b + 1] - P[a + 1]) * f + (Nn[a + 1] + (Nn[b + 1] - Nn[a + 1]) * f) * cn + (Bn[a + 1] + (Bn[b + 1] - Bn[a + 1]) * f) * sn;
        pos[o + 2] = P[a + 2] + (P[b + 2] - P[a + 2]) * f                                              + (Bn[a + 2] + (Bn[b + 2] - Bn[a + 2]) * f) * sn;
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, bufPos);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, pos);

      tiltX += (aimX - tiltX) * 0.04; tiltY += (aimY - tiltY) * 0.04;
      var yaw   = cfg.sway * 0.30 * Math.sin(TAU * 2 * t) + tiltX * 0.16;
      var pitch = cfg.sway * (0.13 * Math.sin(TAU * 3 * t + 1.1) - 0.05) - tiltY * 0.10;

      gl.uniform1f(U.uT, t);
      gl.uniform2f(U.uRot, yaw, pitch);
      gl.uniform2f(U.uScale, halfPx / (W / 2), halfPx / (H / 2));
      gl.uniform1f(U.uPx, Math.max(halfPx / 385, 0.5) * DPR);
      gl.uniform1f(U.uDof, 1.5 * cfg.focus);
      gl.uniform1f(U.uTwinkle, Math.max(0, Math.min(1, cfg.twinkle)));
      gl.uniform1f(U.uSize, cfg.size);
      gl.uniform1f(U.uMax, Math.min(maxPoint, 72 * DPR));
      gl.uniform1f(U.uFade, fade);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.POINTS, 0, drawn);
    }

    /* ---- running ---- */
    var still = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var raf = 0, playing = false, visible = true, origin = performance.now(), held = cfg.loop * 0.07, lost = false;
    function now() { return playing ? (performance.now() - origin) / 1000 : held; }
    function tick() {
      raf = 0;
      if (!playing || !visible || lost) return;
      if (fade < 1) fade = Math.min(1, fade + 0.02);              // light comes up softly on first view
      draw(now());
      raf = requestAnimationFrame(tick);
    }
    function wake() { if (!raf && playing && visible && !lost) raf = requestAnimationFrame(tick); }

    function onMove(e) {
      var box = canvas.getBoundingClientRect();
      aimX = Math.max(-1, Math.min(1, (e.clientX - (box.left + box.width / 2)) / (global.innerWidth / 2)));
      aimY = Math.max(-1, Math.min(1, (e.clientY - (box.top + box.height / 2)) / (global.innerHeight / 2)));
    }
    var hover = cfg.interactive > 0 && !still && global.matchMedia && global.matchMedia('(hover: hover)').matches;
    if (hover) global.addEventListener('pointermove', onMove, { passive: true });

    function onLost(e) { e.preventDefault(); lost = true; }
    function onBack() { lost = false; setup(); resize(); wake(); }
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onBack);

    var ro = global.ResizeObserver ? new ResizeObserver(function () { if (!lost) { resize(); draw(now()); } }) : null;
    if (ro) ro.observe(canvas);
    var io = global.IntersectionObserver ? new IntersectionObserver(function (e) {
      visible = e[e.length - 1].isIntersecting; wake();           // no work while off screen
    }) : null;
    if (io) io.observe(canvas);

    setup(); resize();
    fade = 1;
    draw(now()); wake();

    canvas.dataset.ready = "1";
    return {
      play:  function () { if (!still && !playing) { origin = performance.now() - held * 1000; playing = true; wake(); } },
      pause: function () { if (playing) { held = now(); playing = false; } },
      seek:  function (seconds) { fade = 1; held = seconds; origin = performance.now() - seconds * 1000; if (!lost) draw(seconds); },
      destroy: function () {
        playing = false; if (raf) cancelAnimationFrame(raf);
        if (ro) ro.disconnect(); if (io) io.disconnect();
        if (hover) global.removeEventListener('pointermove', onMove);
        canvas.removeEventListener('webglcontextlost', onLost);
        canvas.removeEventListener('webglcontextrestored', onBack);
        buffers.forEach(function (b) { gl.deleteBuffer(b); });
        shaders.forEach(function (s) { gl.deleteShader(s); });
        if (prog) gl.deleteProgram(prog);
      }
    };
  }

  global.InfinityDots3D = InfinityDots3D;
})(window);
